import { useState } from 'react';

import { Button } from '../../../shared/Button';
import { PortalPollResults } from '../../components/PortalPollResults';
import { isTocaIcon, TocaIcon } from '../../model/components/TocaIcon';
import { usePortalPostEditor } from '../../Post';
import { PositionGridInput } from './PositionGridInput';
import { TocaIconInput } from './TocaIconInput';
import { VoteSliderPollData } from '@lifeserver/shared';

import styles from './PollVoteSliderInput.module.scss';

export interface PollVoteSliderInputProps {
    value: VoteSliderPollData;
    onChange?: (value: VoteSliderPollData) => void;
}

export const PollVoteSliderInput = ({
    value,
    onChange,
}: PollVoteSliderInputProps) => {
    const { isEditingExistingPost, initialPostState } = usePortalPostEditor();
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const isReadOnlyMode =
        isEditingExistingPost && initialPostState === 'ready';

    const handleToggle = () => {
        if (isOpen) {
            setIsClosing(true);
            setTimeout(() => {
                setIsClosing(false);
                setIsOpen(false);
            }, 300);
        } else {
            setIsOpen(true);
        }
    };

    const onPositionChanged = (position: { x: number; y: number }) => {
        onChange?.({
            ...value,
            position,
        });
    };

    const onIconChange = (icon: TocaIcon) => {
        onChange?.({
            ...value,
            alternatives: [
                {
                    ...value.alternatives[0],
                    iconName: icon,
                },
            ],
        });
    };

    const PollConfigurationFields = () => (
        <>
            <PositionGridInput
                value={value.position}
                onChange={onPositionChanged}
                disabled={[
                    { x: -1.0, y: -1.0 },
                    { x: -1.0, y: 0 },
                    { x: -1.0, y: 1.0 },
                    { x: 1.0, y: -1.0 },
                    { x: 1.0, y: 0 },
                    { x: 1.0, y: 1.0 },
                ]}
                bounds={1.0}
            />
            <fieldset>
                <legend>Icon</legend>
                <div className={styles.iconContainer}>
                    <TocaIconInput
                        selectedIcon={
                            isTocaIcon(value.alternatives[0]?.iconName)
                                ? value.alternatives[0]?.iconName
                                : ''
                        }
                        onIconChange={onIconChange}
                    />
                </div>
            </fieldset>
        </>
    );

    return (
        <div className={styles.pollContainer}>
            {isReadOnlyMode && (
                <fieldset>
                    <legend>Results</legend>
                    <PortalPollResults
                        pollId={value.id}
                        alternatives={value.alternatives}
                        pollType="VoteSlider"
                    />
                </fieldset>
            )}

            {isReadOnlyMode ? (
                <fieldset>
                    <legend>Settings</legend>
                    <div className={styles.accordionWrapper}>
                        <Button
                            type="button"
                            onClick={handleToggle}
                            style={{
                                width: '100%',
                                textAlign: 'left',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom:
                                    isOpen || isClosing ? '16px' : '0',
                                backgroundColor: '#E8F4FD',
                                borderColor: '#74B9FF',
                                color: '#2D3436',
                                fontWeight: '500',
                            }}
                        >
                            🔧 View Poll (read only)
                            <span>{isOpen && !isClosing ? '▲' : '▼'}</span>
                        </Button>

                        {(isOpen || isClosing) && (
                            <div
                                className={
                                    isClosing
                                        ? styles.accordionContentClosing
                                        : styles.accordionContent
                                }
                            >
                                <div className={styles.readOnlyOverlay}>
                                    <div className={styles.readOnlyBanner}>
                                        This poll is ready for publication and
                                        can't be changed
                                    </div>
                                    <PollConfigurationFields />
                                </div>
                            </div>
                        )}
                    </div>
                </fieldset>
            ) : (
                <fieldset>
                    <legend>Settings</legend>
                    <PollConfigurationFields />
                </fieldset>
            )}
        </div>
    );
};
